import React from 'react';
import {useIntl} from 'react-intl';
import type {ProjectProposal, Warning} from './types';
import {COLOR_OPTIONS, getAvatarColor, getInitials} from './constants';
import {
	RightPanel,
	PanelLabel,
	ProposalScroll,
	Placeholder,
	PlaceholderContent,
	PlaceholderEmoji,
	PlaceholderTitle,
	PlaceholderDesc,
	EmptyText,
	WarningsBanner,
	WarningItem,
	FieldGroup,
	FieldLabel,
	FieldInput,
	DateRow,
	DateFieldGroup,
	ColorRow,
	ColorCircle,
	BudgetPillRow,
	BudgetPill,
	TeamList,
	TeamMemberRow,
	Avatar,
	TeamMemberInfo,
	TeamMemberName,
	TeamMemberRole,
	HoursBadge,
	OverallocatedIcon,
} from './styles';

interface ProposalPanelProps {
	proposal: ProjectProposal | null;
	warnings: Warning[];
	onProposalChange: (proposal: ProjectProposal) => void;
}

const ProposalPanel: React.FC<ProposalPanelProps> = ({proposal, warnings, onProposalChange}) => {
	const {formatMessage} = useIntl();
	const update = <K extends keyof ProjectProposal>(field: K, value: ProjectProposal[K]) => {
		if (!proposal) return;
		onProposalChange({...proposal, [field]: value});
	};

	return (
		<RightPanel>
			<PanelLabel>{formatMessage({id: 'ai_project_planning.live_preview_label'})}</PanelLabel>
			{!proposal ? (
				<Placeholder>
					<PlaceholderContent>
						<PlaceholderEmoji>📋</PlaceholderEmoji>
						<PlaceholderTitle>
							{formatMessage({id: 'ai_project_planning.proposal_placeholder_title'})}
						</PlaceholderTitle>
						<PlaceholderDesc>
							{formatMessage({id: 'ai_project_planning.proposal_placeholder_desc'})}
						</PlaceholderDesc>
					</PlaceholderContent>
				</Placeholder>
			) : (
				<ProposalScroll>
					{warnings.length > 0 && (
						<WarningsBanner>
							{warnings.map(w => (
								<WarningItem key={`${w.type}-${w.person_id ?? ''}`}>
									<span>⚠</span>
									<span>{w.message}</span>
								</WarningItem>
							))}
						</WarningsBanner>
					)}

					<FieldGroup>
						<FieldLabel>{formatMessage({id: 'ai_project_planning.field_project_name'})}</FieldLabel>
						<FieldInput type="text" value={proposal.name} onChange={e => update('name', e.target.value)} />
					</FieldGroup>

					<FieldGroup>
						<FieldLabel>{formatMessage({id: 'ai_project_planning.field_color'})}</FieldLabel>
						<ColorRow>
							{COLOR_OPTIONS.map(c => (
								<ColorCircle
									key={c}
									color={c}
									selected={proposal.color === c}
									onClick={() => update('color', c)}
									title={c}
								>
									{proposal.color === c && '✓'}
								</ColorCircle>
							))}
						</ColorRow>
					</FieldGroup>

					<FieldGroup>
						<FieldLabel>{formatMessage({id: 'ai_project_planning.field_project_id'})}</FieldLabel>
						<FieldInput
							type="text"
							value={formatMessage({id: 'ai_project_planning.field_auto_generated'})}
							readOnly
						/>
					</FieldGroup>

					<DateRow>
						<DateFieldGroup>
							<FieldLabel>{formatMessage({id: 'ai_project_planning.field_start_date'})}</FieldLabel>
							<FieldInput
								type="date"
								value={proposal.start_date}
								onChange={e => update('start_date', e.target.value)}
							/>
						</DateFieldGroup>
						<DateFieldGroup>
							<FieldLabel>{formatMessage({id: 'ai_project_planning.field_end_date'})}</FieldLabel>
							<FieldInput
								type="date"
								value={proposal.end_date}
								onChange={e => update('end_date', e.target.value)}
							/>
						</DateFieldGroup>
					</DateRow>

					<FieldGroup>
						<FieldLabel>{formatMessage({id: 'ai_project_planning.field_budget_type'})}</FieldLabel>
						<BudgetPillRow>
							{[
								{key: 'FIXED_PRICE', labelId: 'ai_project_planning.budget_fixed_price'},
								{key: 'TIME_AND_MATERIALS', labelId: 'ai_project_planning.budget_time_materials'},
								{key: 'NON_BILLABLE', labelId: 'ai_project_planning.budget_non_billable'},
								{key: 'RETAINER', labelId: 'ai_project_planning.budget_retainer'},
							].map(({key, labelId}) => (
								<BudgetPill
									key={key}
									selected={proposal.budget_type === key}
									onClick={() => update('budget_type', key)}
								>
									{formatMessage({id: labelId})}
								</BudgetPill>
							))}
						</BudgetPillRow>
					</FieldGroup>

					<FieldGroup>
						<FieldLabel>{formatMessage({id: 'ai_project_planning.team_ai_assigned'})}</FieldLabel>
						{(proposal.team_members ?? []).length === 0 ? (
							<EmptyText>{formatMessage({id: 'ai_project_planning.no_team_members'})}</EmptyText>
						) : (
							<TeamList>
								{(proposal.team_members ?? []).map(member => (
									<TeamMemberRow key={member.person_id}>
										<Avatar bg={getAvatarColor(member.name)}>{getInitials(member.name)}</Avatar>
										<TeamMemberInfo>
											<TeamMemberName>{member.name}</TeamMemberName>
											<TeamMemberRole>{member.role}</TeamMemberRole>
										</TeamMemberInfo>
										<HoursBadge>
											{formatMessage(
												{id: 'ai_project_planning.hours_available'},
												{hours: member.available_hours}
											)}
										</HoursBadge>
										{member.is_overallocated && (
											<OverallocatedIcon title={formatMessage({id: 'ai_project_planning.overallocated'})}>
												⚠
											</OverallocatedIcon>
										)}
									</TeamMemberRow>
								))}
							</TeamList>
						)}
					</FieldGroup>
				</ProposalScroll>
			)}
		</RightPanel>
	);
};

export default ProposalPanel;
